import { PartImageCandidate, PartImageIdentification, PartImageItem, PartImageItemOverLimit } from '@/api/generatedApi';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { useCreateBatchFromPartImageItems } from '@/hooks/api/part-finder-image-search/useCreateBatchFromPartImageItems';
import {
  useMergePartImageItems,
  useSplitPartImageItem,
  useUnmergePartImageItem,
} from '@/hooks/api/part-finder-image-search/useEditPartImageItems';
import { getApiError } from '@/types/apiError';
import { pluralize } from '@/utils/format';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { IMAGE_MATCH_CONFIDENCE, scoreToPercent } from '@/components/PartFinder/confidence/confidence';
import { PercentConfidencePill } from '@/components/PartFinder/confidence/ConfidencePill';
import { PartImageItemClip, usePartImageItemClips } from './partImageItemClips';
import { readTextLabel } from './partImageReadText';

const NO_PICK = '';
const NO_MATCH_HINT = 'No FLIS parts found for this item. Search it in its own photo, or merge it with another item.';
const ITEM_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const ItemCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '12px',
  padding: '12px',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
}));

const itemKey = (item: PartImageItem): string => `${item.photoNumbers.join('-')}:${item.description}`;

export const itemLabel = (index: number): string => `Item ${ITEM_LETTERS[index] ?? index + 1}`;

const photoList = (photoNumbers: number[]): string =>
  `${photoNumbers.length === 1 ? 'photo' : 'photos'} ${photoNumbers.join(', ')}`;

const CandidateOption = ({ candidate }: { candidate: PartImageCandidate }) => (
  <Stack direction='row' gap='8px' alignItems='center' sx={{ minWidth: 0, width: '100%' }}>
    <Typography variant='body2' noWrap sx={{ flex: 1, minWidth: 0 }}>
      {candidate.nsn} {candidate.itemName ?? ''}
    </Typography>
    <PercentConfidencePill percent={scoreToPercent(candidate.score)} explanation={IMAGE_MATCH_CONFIDENCE} />
  </Stack>
);

const createBatchLabel = (included: number, total: number): string =>
  `Create batch (${included} of ${pluralize(total, 'item', 'items')})`;

const CLIP_HEIGHT = 88;
const CLIP_MAX_WIDTH = 132;

const ClipFrame = styled('img')(({ theme }) => ({
  display: 'block',
  height: CLIP_HEIGHT,
  maxWidth: CLIP_MAX_WIDTH,
  objectFit: 'contain',
  borderRadius: '6px',
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
}));

const ItemClips = ({ clips, label }: { clips: PartImageItemClip[]; label: string }) => (
  <Stack
    gap='6px'
    alignItems='center'
    sx={{ flexShrink: 0, width: CLIP_MAX_WIDTH }}
    data-testid='part-image-item-clips'
  >
    {clips.map((clip, index) => (
      <Tooltip
        key={clip.url}
        placement='right'
        title={<Box component='img' src={clip.url} alt='' sx={{ display: 'block', maxWidth: 360, maxHeight: 360 }} />}
      >
        <ClipFrame src={clip.url} alt={`${label}, photo ${clip.photoNumber}, clip ${index + 1}`} />
      </Tooltip>
    ))}
  </Stack>
);

interface MergeMenuProperties {
  index: number;
  items: PartImageItem[];
  disabled: boolean;
  onMerge: (otherIndex: number) => void;
}

const MergeMenu = ({ index, items, disabled, onMerge }: MergeMenuProperties) => {
  const [anchor, setAnchor] = useState<HTMLElement>();
  const choose = (otherIndex: number) => {
    setAnchor(undefined);
    onMerge(otherIndex);
  };
  return (
    <>
      <Button
        size='small'
        disabled={disabled}
        onClick={event => setAnchor(event.currentTarget)}
        sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        data-testid='part-image-item-merge'
      >
        Merge with…
      </Button>
      <Menu anchorEl={anchor} open={anchor !== undefined} onClose={() => setAnchor(undefined)}>
        {items.flatMap((other, otherIndex) =>
          otherIndex === index
            ? []
            : [
                <MenuItem key={itemKey(other)} onClick={() => choose(otherIndex)} sx={{ maxWidth: 480 }}>
                  <Typography variant='body2' noWrap>
                    <strong>{itemLabel(otherIndex)}</strong> {other.description}
                  </Typography>
                </MenuItem>,
              ]
        )}
      </Menu>
    </>
  );
};

interface ItemActionButtonProperties {
  label: string;
  testId: string;
  disabled: boolean;
  onClick: () => void;
}

const ItemActionButton = ({ label, testId, disabled, onClick }: ItemActionButtonProperties) => (
  <Button
    size='small'
    disabled={disabled}
    onClick={onClick}
    sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
    data-testid={testId}
  >
    {label}
  </Button>
);

interface IncludeCheckboxProperties {
  index: number;
  included: boolean;
  picked: boolean;
  busy: boolean;
  onToggle: () => void;
}

const IncludeCheckbox = ({ index, included, picked, busy, onToggle }: IncludeCheckboxProperties) => (
  <Tooltip title={picked ? '' : 'Pick a part to include this item'} placement='top'>
    <Box component='span' sx={{ alignSelf: 'flex-start' }}>
      <Checkbox
        checked={included}
        onChange={onToggle}
        disabled={busy || !picked}
        slotProps={{ input: { 'aria-label': `Include ${itemLabel(index)} in batch` } }}
        sx={{ p: 0.5 }}
      />
    </Box>
  </Tooltip>
);

interface ItemRowProperties {
  item: PartImageItem;
  items: PartImageItem[];
  clips: PartImageItemClip[];
  index: number;
  pick: string;
  included: boolean;
  busy: boolean;
  onPick: (niin: string) => void;
  onToggle: () => void;
  onMerge: (otherIndex: number) => void;
  onSplit: () => void;
  onUnmerge: () => void;
}

const ItemRow = ({
  item,
  items,
  clips,
  index,
  pick,
  included,
  busy,
  onPick,
  onToggle,
  onMerge,
  onSplit,
  onUnmerge,
}: ItemRowProperties) => (
  <ItemCard data-testid='part-image-item'>
    <IncludeCheckbox index={index} included={included} picked={pick !== NO_PICK} busy={busy} onToggle={onToggle} />
    {clips.length > 0 && <ItemClips clips={clips} label={itemLabel(index)} />}
    <Stack gap='8px' sx={{ flex: 1, minWidth: 0 }}>
      <Stack direction='row' gap='8px' alignItems='baseline' flexWrap='wrap'>
        <Typography variant='subtitle2'>{itemLabel(index)}</Typography>
        <Typography variant='caption' color='text.secondary'>
          {photoList(item.photoNumbers)}
        </Typography>
        {item.description && <Typography variant='body2'>{item.description}</Typography>}
      </Stack>
      {item.readText.length > 0 && (
        <Stack direction='row' gap='6px' flexWrap='wrap'>
          {item.readText.map(text => (
            <Chip key={`${text.kind}:${text.text}`} size='small' label={readTextLabel(text)} />
          ))}
        </Stack>
      )}
      <Stack direction='row' gap='8px' alignItems='center'>
        <TextField
          select
          size='small'
          fullWidth
          label='Part'
          value={pick}
          disabled={busy || item.candidates.length === 0}
          helperText={item.candidates.length === 0 ? NO_MATCH_HINT : undefined}
          onChange={event => onPick(event.target.value)}
          slotProps={{ htmlInput: { 'data-testid': 'part-image-item-pick' } }}
        >
          <MenuItem value={NO_PICK} disabled>
            {item.candidates.length === 0 ? 'No FLIS match' : 'Pick a part'}
          </MenuItem>
          {item.candidates.map(candidate => (
            <MenuItem key={candidate.niin} value={candidate.niin}>
              <CandidateOption candidate={candidate} />
            </MenuItem>
          ))}
        </TextField>
        {items.length > 1 && <MergeMenu index={index} items={items} disabled={busy} onMerge={onMerge} />}
        {item.merged && (
          <ItemActionButton label='Unmerge' testId='part-image-item-unmerge' disabled={busy} onClick={onUnmerge} />
        )}
        {!item.merged && item.photoNumbers.length > 1 && (
          <ItemActionButton label='Split' testId='part-image-item-split' disabled={busy} onClick={onSplit} />
        )}
      </Stack>
    </Stack>
  </ItemCard>
);

const topPick = (item: PartImageItem): string => item.suggestedNiin ?? item.candidates[0]?.niin ?? NO_PICK;

const replacedAt = <T,>(values: T[], index: number, value: T): T[] =>
  values.map((existing, position) => (position === index ? value : existing));

const ItemsOverLimit = ({ items }: { items: PartImageItemOverLimit[] }) => {
  const [open, setOpen] = useState(false);
  return (
    <Alert
      severity='warning'
      data-testid='part-image-items-over-limit'
      action={
        <Button
          color='inherit'
          size='small'
          onClick={() => setOpen(!open)}
          data-testid='part-image-items-over-limit-toggle'
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      }
    >
      {pluralize(items.length, 'more item was', 'more items were')} found but not searched. Search them in another photo
      search.
      <Collapse in={open}>
        <Box component='ul' sx={{ m: 0, mt: 1, pl: 2 }}>
          {items.map((item, index) => (
            <li key={`${item.photoNumbers.join('-')}-${index}`}>
              {item.description || 'A part'} ({photoList(item.photoNumbers)})
            </li>
          ))}
        </Box>
      </Collapse>
    </Alert>
  );
};

export interface MultipleItemsResultProperties {
  identification: PartImageIdentification;
  images: File[];
  onBatchCreated: (batchId: string) => void;
}

export const MultipleItemsResult = ({ identification, images, onBatchCreated }: MultipleItemsResultProperties) => {
  const { items, itemsOverLimit } = identification;
  const clips = usePartImageItemClips(images, items);
  const [picks, setPicks] = useState(() => items.map(item => topPick(item)));
  const [included, setIncluded] = useState(() => items.map(item => item.suggestedNiin !== undefined));
  const merge = useMergePartImageItems(identification.id);
  const split = useSplitPartImageItem(identification.id);
  const unmerge = useUnmergePartImageItem(identification.id);
  const createBatch = useCreateBatchFromPartImageItems(identification.id);
  const busy = merge.isPending || split.isPending || unmerge.isPending || createBatch.isPending;
  const selections = picks.flatMap((niin, itemIndex) =>
    niin !== NO_PICK && included[itemIndex] ? [{ itemIndex, niin }] : []
  );

  const toggle = (index: number) => setIncluded(current => replacedAt(current, index, !current[index]));
  const pickFor = (index: number) => (niin: string) => {
    setPicks(current => replacedAt(current, index, niin));
    setIncluded(current => replacedAt(current, index, true));
  };
  const handleCreateBatch = () => createBatch.mutate(selections, { onSuccess: onBatchCreated });

  const apiError = getApiError(
    merge.error ?? split.error ?? unmerge.error ?? createBatch.error ?? undefined,
    'Could not update items'
  );

  return (
    <Stack gap='12px' sx={{ flex: { md: 1 }, minHeight: 0 }} data-testid='part-image-items'>
      <Typography variant='subtitle1' data-testid='part-image-items-heading'>
        Found {pluralize(items.length, 'item', 'items')} in {pluralize(identification.imageCount, 'photo', 'photos')}
      </Typography>
      {itemsOverLimit.length > 0 && <ItemsOverLimit items={itemsOverLimit} />}
      <ApiErrorAlert error={apiError} />
      <Stack gap='8px' sx={{ overflowY: { md: 'auto' }, flex: { md: 1 }, minHeight: 0, pr: '4px' }}>
        {items.map((item, index) => (
          <ItemRow
            key={`${itemKey(item)}-${index}`}
            item={item}
            items={items}
            clips={clips[index] ?? []}
            index={index}
            pick={picks[index] ?? NO_PICK}
            included={included[index] ?? false}
            busy={busy}
            onPick={pickFor(index)}
            onToggle={() => toggle(index)}
            onMerge={otherIndex => merge.mutate([index, otherIndex])}
            onSplit={() => split.mutate(index)}
            onUnmerge={() => unmerge.mutate(index)}
          />
        ))}
      </Stack>
      <Stack direction='row' gap='8px' alignItems='center'>
        <Typography variant='caption' color='text.secondary'>
          Checked items with a part picked go into the batch, one report each.
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          variant='contained'
          disabled={busy || selections.length === 0}
          onClick={handleCreateBatch}
          data-testid='part-image-items-create-batch'
        >
          {createBatchLabel(selections.length, items.length)}
        </Button>
      </Stack>
    </Stack>
  );
};
